import { Link } from 'react-router-dom';
import { comics, categories, faqItems } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useState } from 'react';

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6" id="faq">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
            常见问题
          </h2>
          <p className="mt-3 text-foreground-500 text-sm md:text-base">
            关于韩漫画网，您可能想了解的
          </p>
        </div>
        <div className="space-y-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-150"
                aria-expanded={openIndex === index}
              >
                <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                  {item.question}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-5 px-5' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const featuredComics = comics.filter((c) => c.featured);
  const hotComics = comics.filter((c) => c.isHot);
  const newComics = comics.filter((c) => c.isNew);
  const allComics = comics;

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫画网 - 免费在线观看韩国漫画',
    description: '韩漫画网支持免费在线观看韩国漫画和好看的韩漫大全，资源分类细致搜索方便，帮助您快速发现热门漫画作品。',
    url: 'https://hanmanhua.com',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'https://hanmanhua.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[420px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20artistic%20illustration%20background%2C%20abstract%20comic%20style%20with%20soft%20warm%20gradients%20of%20coral%20and%20cream%20tones%2C%20stylized%20flowing%20lines%20reminiscent%20of%20comic%20panels%2C%20dreamy%20ethereal%20atmosphere%2C%20clean%20modern%20Korean%20digital%20art%20aesthetic%2C%20no%20text%2C%20artistic%20composition&width=1920&height=900&seq=hanman-hero-bg&orientation=landscape"
            alt="韩漫画网 - 免费韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6 py-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-background-50 leading-tight">
              免费在线观看韩国漫画
            </h1>
            <p className="mt-5 text-sm md:text-lg text-background-200/90 max-w-xl mx-auto leading-relaxed">
              韩漫画网汇集海量优质韩国漫画资源，分类细致、每日更新<br className="hidden md:block" />
              让您轻松发现和畅享喜欢的韩漫作品
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/category/all"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full text-sm md:text-base font-medium hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                浏览分类
              </Link>
            </div>
            <div className="mt-10 flex items-center justify-center gap-6 text-xs md:text-sm text-background-200/80">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-checkbox-circle-line"></i>
                完全免费
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-refresh-line"></i>
                每日更新
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-smartphone-line"></i>
                手机适配
              </span>
            </div>
          </div>
        </section>

        {/* Featured Comics */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                  精选推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  编辑精选的高质量韩漫作品
                </p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            <Link
              to="/category/all"
              className="sm:hidden flex items-center justify-center gap-1 mt-8 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Category Grid */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                漫画分类
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                按类型浏览，快速找到喜欢的漫画
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.slice(1).map((cat, index) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-6 md:p-8 bg-background-50 rounded-lg hover:bg-background-100 transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full transition-all duration-300 ${
                    index % 2 === 0
                      ? 'bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 dark:group-hover:text-foreground-950'
                      : 'bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-background-50 dark:group-hover:text-foreground-950'
                  }`}>
                    <i className={`${cat.icon} text-2xl md:text-3xl`}></i>
                  </div>
                  <h3 className="mt-4 text-sm md:text-base font-semibold text-foreground-900 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-400 text-center">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                  热门漫画
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  大家都在追的热门韩漫
                </p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
              >
                更多热门 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                  最新上架
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  刚刚更新的新作漫画
                </p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
              >
                更多新作 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* All Comics Section */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                全部漫画
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                探索韩漫画网的全部漫画资源
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {allComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                查看全部漫画 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-20 md:py-28 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20art%20background%2C%20warm%20atmospheric%20illustration%20with%20soft%20blended%20colors%20of%20peach%20and%20cream%2C%20stylized%20comic%20elements%20floating%2C%20dreamy%20reading%20atmosphere%2C%20clean%20Korean%20digital%20art%20style%2C%20no%20text&width=1920&height=600&seq=hanman-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-background-50">
              立即开始您的韩漫之旅
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed">
              海量韩国漫画资源，完全免费在线观看，每日同步更新
            </p>
            <div className="mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-10 py-3.5 bg-accent-500 text-background-50 dark:text-foreground-950 rounded-full text-sm md:text-base font-semibold hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                免费观看 <i className="ri-play-circle-line text-lg"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQSection />
      </main>

      <Footer />
    </>
  );
}